"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMinimalValidDeviceObject = exports.isValidAWSDeviceObject = exports.getWhyDeviceInvalid = exports.isValidDeviceObject = exports.isBasicDeviceObject = exports.isLNSResolveableDeviceObject = exports.isASFSupportingDeviceType = exports.MandatoryDeviceKeys = exports.DeviceAppCycleMode = exports.readableDeviceState = exports.DeviceState = exports.DeviceProvisioningState = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
var DeviceProvisioningState;
(function (DeviceProvisioningState) {
    DeviceProvisioningState["NOT_INVENTORIZED"] = "NOT_INVENTORIZED";
    DeviceProvisioningState["INVENTORY"] = "INVENTORY";
    DeviceProvisioningState["ALLOCATED"] = "ALLOCATED";
})(DeviceProvisioningState || (exports.DeviceProvisioningState = DeviceProvisioningState = {}));
/**
 * Approximate flow, assuming a production environment:
 *
 * 1. {@link DeviceState.ORDERED | `Ordered`}
 * 1. {@link DeviceState.MANUFACTURING | `Manufacturing`}
 * 1. {@link DeviceState.JOINED | `Joined`} as part of the manufacturing validation and testing process
 * 1. {@link DeviceState.TRANSITIN | `Transit in`}
 * 1. {@link DeviceState.INVENTORY | `Inventory`} with us or reseller
 * 1. {@link DeviceState.ORDEREDCUSTOMER | `Ordered By Customer`} as the end-user
 * 1. {@link DeviceState.TRANSITOUT | `Transit Out`}
 * 1. {@link DeviceState.WITHCUSTOMER | `With Customer`}
 * 1. {@link DeviceState.COMMISSIONED | `Commissioned`} when installed and on the network
 * 1. {@link DeviceState.CALIBRATED | `Calibrated`} if required by the device
 *
 * The device is now in a working state
 *
 * If the device experiences an issue that requires an engineer to diagnose and fix:
 *
 * 1. {@link DeviceState.DECOMMISSONED | `Decommissioned`} by end-user
 * 1. {@link DeviceState.TRANSITIN | `Transit In`}
 * 1. {@link DeviceState.WITHENGINEER | `With Engineer`} for diagnosis and repair
 * 1. {@link DeviceState.RETIRED | `Retired`} if unable to be repaired, or
 * 1. {@link DeviceState.TRANSITOUT | `Transit Out`} back to the end-user
 *
 * Unused states:
 *
 * - {@link DeviceState.UNKNOWN | `Unknown`}
 * - {@link DeviceState.SUSPENDED | `Suspended`}
 */
var DeviceState;
(function (DeviceState) {
    DeviceState["ORDERED"] = "ORDERED";
    DeviceState["MANUFACTURING"] = "MANUFACTURING";
    DeviceState["JOINED"] = "JOINED";
    DeviceState["TRANSITIN"] = "TRANSITIN";
    DeviceState["INVENTORY"] = "INVENTORY";
    DeviceState["ORDEREDCUSTOMER"] = "ORDEREDCUSTOMER";
    DeviceState["TRANSITOUT"] = "TRANSITOUT";
    DeviceState["WITHCUSTOMER"] = "WITHCUSTOMER";
    DeviceState["COMMISSIONED"] = "COMMISSIONED";
    DeviceState["CALIBRATED"] = "CALIBRATED";
    DeviceState["DECOMMISSONED"] = "DECOMMISSONED";
    DeviceState["WITHENGINEER"] = "WITHENGINEER";
    DeviceState["RETIRED"] = "RETIRED";
    DeviceState["SUSPENDED"] = "SUSPENDED";
    DeviceState["UNKNOWN"] = "UNKNOWN";
})(DeviceState || (exports.DeviceState = DeviceState = {}));
const readableDeviceState = (key) => {
    switch (key) {
        case DeviceState.ORDERED:
            return 'Ordered';
        case DeviceState.MANUFACTURING:
            return 'Manufacturing';
        case DeviceState.JOINED:
            return 'Joined';
        case DeviceState.TRANSITIN:
            return 'Transit In';
        case DeviceState.INVENTORY:
            return 'Inventory';
        case DeviceState.ORDEREDCUSTOMER:
            return 'Ordered By Customer';
        case DeviceState.TRANSITOUT:
            return 'Transit Out';
        case DeviceState.WITHCUSTOMER:
            return 'With Customer';
        case DeviceState.COMMISSIONED:
            return 'Commissioned';
        case DeviceState.CALIBRATED:
            return 'Calibrated';
        case DeviceState.DECOMMISSONED:
            return 'Decommissioned';
        case DeviceState.WITHENGINEER:
            return 'With Engineer';
        case DeviceState.RETIRED:
            return 'Retired';
        case DeviceState.SUSPENDED:
            return 'Suspended';
        case DeviceState.UNKNOWN:
            return 'Unknown';
    }
};
exports.readableDeviceState = readableDeviceState;
/**
 * When changing mode, **always** send a downlink alongside updating the {@link Device | `Device`} object!
 */
var DeviceAppCycleMode;
(function (DeviceAppCycleMode) {
    /** Default, with user-configurable app cycle time, commonly defaults to 15 minutes */
    DeviceAppCycleMode["Normal"] = "normal";
    /** Alternate, with non-configurable 1-minute app cycle time */
    DeviceAppCycleMode["Rapid Response Mode"] = "rapid";
})(DeviceAppCycleMode || (exports.DeviceAppCycleMode = DeviceAppCycleMode = {}));
exports.MandatoryDeviceKeys = [
    'eui',
    'appID',
    'appKey',
    'networkKey',
    'deviceId',
    'deviceType',
    'appEui',
    'firmwareLevel',
    'regionalPlan',
];
const isASFSupportingDeviceType = (vob) => {
    return (0, exports.isValidDeviceObject)(vob) && (0, iotv_v3_types_1.deviceSupportsIoTVFeature)({ device: vob, feature: 'ASF' });
};
exports.isASFSupportingDeviceType = isASFSupportingDeviceType;
/** type check for LNS resolution and downlink sending, to deal with legacy LNS devices where appKey, appEui are not recorded */
const isLNSResolveableDeviceObject = (vob) => {
    const { eui, networkKey, deviceId, appID } = vob ?? {};
    return !!((0, iotv_v3_types_1.isVOBofType)('Device', vob) && eui === vob.id && appID && networkKey);
};
exports.isLNSResolveableDeviceObject = isLNSResolveableDeviceObject;
const typecheck = { id: '', pk: '', sk: '', type: 'Device', eui: '', networkKey: iotv_v3_types_1.Networks.Interceptor, deviceType: iotv_v3_types_1.DeviceTypes.AgriTel };
/** returns boolen indicating the device has basic attributes required for network processsing
 * This is used where device have been discovered eg on a public network and the provisioning keys are not available.
 * Checks on objects attributes: eui,  networkKey or deviceId, deviceType
 * Note state is no longer mandatory, it is only required for state management.
 */
const isBasicDeviceObject = (vob) => {
    const { eui, networkKey, deviceType } = vob ?? {};
    return !!((0, iotv_v3_types_1.isVOBofType)('Device', vob) && eui === vob.id && networkKey && deviceType);
};
exports.isBasicDeviceObject = isBasicDeviceObject;
/** returns boolen indicating the device has all the key attributes required for system processing.
 * Checks on objects attributes: eui, appKey, networkKey or deviceId, deviceType
 * Note state is no longer mandatory, it is only required for state management.
 */
const isValidDeviceObject = (vob) => {
    const { eui, appKey, networkKey, deviceId, deviceType, appEui, appID } = vob ?? {};
    return !!((0, iotv_v3_types_1.isValidBusinessObject)(vob) && eui === vob.id && appKey && appEui && networkKey && deviceType);
};
exports.isValidDeviceObject = isValidDeviceObject;
/* Companion fn to the one above that explains why your device is invalid */
const getWhyDeviceInvalid = (vob) => {
    const { eui, appKey, networkKey, deviceId, deviceType, appEui, appID } = vob ?? {};
    const valid = (0, iotv_v3_types_1.isValidBusinessObject)(vob);
    let problems = [];
    if (eui !== vob.id) {
        problems.push(`Device EUI (${eui}) != Device Id (${vob.id})`);
    }
    if (!appKey) {
        problems.push('No appkey');
    }
    if (!appEui) {
        problems.push('No app EUI');
    }
    if (!networkKey) {
        problems.push('No network key');
    }
    if (!deviceType) {
        problems.push('No device type');
    }
    return { valid, problems, vob };
};
exports.getWhyDeviceInvalid = getWhyDeviceInvalid;
/** Checks if the region is valid and if the network key is AWS */
const isValidAWSDeviceObject = (vob) => {
    return (0, exports.isValidDeviceObject)(vob) && (0, iotv_v3_types_1.isValidRFRegion)(vob.regionalPlan) && vob.networkKey === iotv_v3_types_1.Networks.AWS_LPWAN;
};
exports.isValidAWSDeviceObject = isValidAWSDeviceObject;
/** returns a stripped device object which can be used to retrieve the full current version and has all necessary keys for system processing
 * used in DeviceControlTransaction to minimize storage.
 */
const getMinimalValidDeviceObject = (item) => {
    if ((0, exports.isValidDeviceObject)(item)) {
        return (0, iotv_v3_types_1.stripNode)(item, exports.MandatoryDeviceKeys);
    }
    return null;
};
exports.getMinimalValidDeviceObject = getMinimalValidDeviceObject;
